import { Component, Element, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import {
  applySchemeDelegate,
  ColorableComponent,
  getColorOfNearestBackground,
  setCssVariablesDelegate
} from "../../common/colorable-component";
import {
  ContentAggregatorGridSize,
  ContentAggregatorLayout,
  ContentAggregatorItemLayoutAlignment,
  ContentAggregatorSchemeDefinition,
  ContentAggregatorSchemeName,
  NumberOfItemsPerCarouselWindow
} from "./constants/content-aggregator-types";
import {
  CONTENT_AGGREGATOR_LARGE_GRID_SIZE,
  CONTENT_AGGREGATOR_LAYOUT_CAROUSEL,
  CONTENT_AGGREGATOR_LAYOUT_LIST,
  CONTENT_AGGREGATOR_LAYOUT_LIST_EXPANDED,
  CONTENT_AGGREGATOR_LAYOUT_TILE,
  CONTENT_AGGREGATOR_ITEM_LAYOUT_ALIGNMENT_LEFT,
  CONTENT_AGGREGATOR_SCHEME_CONFIG,
  CONTENT_AGGREGATOR_SCHEME_MAP,
  EIGHT_ITEMS,
  FOUR_ITEMS,
  SIX_ITEMS,
  THREE_ITEMS,
  TWELVE_ITEMS,
  TWO_ITEMS,
  CONTENT_AGGREGATOR_ITEM_LAYOUT_ALIGNMENT_ALTERNATE,
  CONTENT_AGGREGATOR_ITEM_LAYOUT_ALIGNMENT_RIGHT
} from "./constants/content-aggregator-constants";
import { detectSlotChanges } from "../../utils/slot-helper";
import { HeadlineTag } from "../headline/constants/headline-types";
import { ContentAggregatorSchemeConfiguration } from "../../constants/colors";
import { isDesktopLarge, isDesktopMedium, isDesktopSmall, isTablet } from "../../utils/screen-helper";
import pfx from "../../utils/style-class-prefix-helper";
import { ContentAggregatorItemHeadlineSize } from "../content-aggregator-item/constants/content-aggregator-item-types";
import {
  CONTENT_AGGREGATOR_ITEM_LARGE_HEADLINE,
  CONTENT_AGGREGATOR_ITEM_LAYOUT_LIST,
  CONTENT_AGGREGATOR_ITEM_LAYOUT_LIST_EXPANDED,
  CONTENT_AGGREGATOR_ITEM_LAYOUT_TILE
} from "../content-aggregator-item/constants/content-aggregator-item-constants";
import {
  CONTENT_AGGREGATOR_COLORFUL_VARIANT_COLORS,
  CONTENT_AGGREGATOR_TILE_DEFAULT_COLOR_SCHEME,
  CONTENT_AGGREGATOR_TILE_BRIGHT_COLOR_SCHEME,
  HeadlineAlignment,
  CONTENT_AGGREGATOR_HEADLINE_ALIGNMENT,
  CONTENT_AGGREGATOR_BACKGROUND_FOR_COLORFUL_VARIANT
} from "../../constants/global-constants";

/**
 * @slot [unnamed] - holds any number of HTML elements.
 */
@Component({
  tag: "eon-ui-content-aggregator",
  styleUrl: "content-aggregator.scss",
  shadow: true
})
export class ContentAggregator
  implements ColorableComponent<ContentAggregatorSchemeConfiguration<ContentAggregatorSchemeDefinition>> {
  schemeConfig: ContentAggregatorSchemeConfiguration<ContentAggregatorSchemeDefinition> = CONTENT_AGGREGATOR_SCHEME_CONFIG;
  schemeMap = CONTENT_AGGREGATOR_SCHEME_MAP;
  contentAggregatorItems: Element[] = [];
  carouselContainer: any;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: ContentAggregatorSchemeDefinition;
  @State() newsTeaserWrapper = [];
  @State() carouselItemWrapper = [];
  @State() tileRowWrapper = [];
  @State() tileItemWrapper = [];
  @State() carouselItems: Element[] = [];
  @State() aggregatorHeadlineHtmlTag: string;
  @State() aggregatorHeadlineSize: string;

  @State() visibleTileItems: Element[] = [];
  @State() itemsPerGrid: number;
  @State() lastTileIndex: number;

  @Element() hostElement: HTMLEonUiContentAggregatorElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: ContentAggregatorSchemeName;

  /**
   * Sets a layout for a Aggregator varient component
   * @example carousel | list | list_expanded
   */
  @Prop({ reflect: true, mutable: true }) layout: ContentAggregatorLayout = CONTENT_AGGREGATOR_LAYOUT_CAROUSEL;

  /**
   * OnDesktop Large and Medium (>= 1600) the News-Teaser count per carousel-item can be 3 or 2.
   * options: 3 | 2
   */
  @Prop({ reflect: true }) countOnDesktop: NumberOfItemsPerCarouselWindow = TWO_ITEMS;

  /**
   * Property will hide and show the content/abstract of all the News-Teasers in carousal component
   */
  @Prop({ reflect: true, mutable: true }) showAbstract: boolean = true;

  /**
   * This property hides or shows the date property of all the News-Teasers in carousal component
   */
  @Prop({ reflect: true, mutable: true }) dateInput: boolean = true;

  /**
   * Property will hide and show the time format in date section of all the News-Teasers in carousal component
   */
  @Prop({ reflect: true, mutable: true }) hideTime: boolean = true;

  /**
   * timeZone property for uniform standard time, gives flexibility to authors to edit the timeZone
   * And implement the same all the News-Teasers in carousal component
   * @example: CET/CEST
   */
  @Prop({ mutable: true }) timeZone: string;

  /**
   * twelveHourFormat depicts whether date should be of 12 hours format or 24 hours format
   * Of all the News-Teasers in carousal component
   */
  @Prop({ reflect: true, mutable: true }) twelveHourFormat: boolean = false;

  /**
   * The size of the headline inside the teaser
   * @example: large | small
   */
  // eslint-disable-next-line @stencil/decorators-style
  @Prop({ reflect: true, mutable: true })
  size: ContentAggregatorItemHeadlineSize = CONTENT_AGGREGATOR_ITEM_LARGE_HEADLINE;

  /**
   * The size of the grid layout
   * determines the initial loaded items
   * @example: large | small
   */
  // eslint-disable-next-line @stencil/decorators-style
  @Prop({ reflect: true, mutable: true })
  gridSize: ContentAggregatorGridSize = CONTENT_AGGREGATOR_LARGE_GRID_SIZE;

  /**
   * The alignment of the headline
   * @example: left | right
   */
  @Prop() align: HeadlineAlignment = CONTENT_AGGREGATOR_HEADLINE_ALIGNMENT;

  /**
   * Determines the layout of the expanded list item's content when variant is set to `list_expanded`
   * @example default: left
   */
  @Prop() alignLayout: ContentAggregatorItemLayoutAlignment = CONTENT_AGGREGATOR_ITEM_LAYOUT_ALIGNMENT_LEFT;

  /**
   * If set, slides will switch after the defined time in miliseconds.
   * Must be at least 6000.
   */
  @Prop() autoplay: number = 6000;

  /**
   * Property will change color scheme of News Teaser on basis of background color scheme
   * By default the News Teasers will be colourfull on white background scheme
   */
  @Prop({ reflect: true, mutable: true }) whiteColorScheme: boolean = false;

  /**
   * height property will set height to 100% of all the news-teaserif true
   */
  @Prop({ reflect: true, mutable: true }) sameHeight: boolean = true;

  /**
   * headlineHtmlTag property is used for SEO-Optimisation.<br>
   * If this property is used and fill-out, this property is used for HTML-Tag for headline.
   */
  @Prop() headlineHtmlTag: HeadlineTag;

  /**
   * headlineCharacterLimit will limit the charaters of headline to the given value
   * @disableControlInStorybook true
   * @example default: 50
   */
  @Prop({ reflect: true }) headlineCharacterLimit: number = 50;

  /**
   * copyCharacterLimit will limit the charaters of copy text to the given value
   * @disableControlInStorybook true
   * @example default: 125
   */
  @Prop({ reflect: true }) copyCharacterLimit: number = 125;

  /**
   * Comma seperated list of page of page sizes
   */
  @Prop() pageSizes: string = "5,10,20,50,100";

  /**
   * Provide to total number of items.
   * Use this if you want to use this component with a backend api with paging.
   */
  @Prop() totalItems: number;

  /**
   * Determines whether the list expanded items will be showed with alternating background.
   */
  @Prop() zebra: boolean;

  /**
   * A string set as content of the button.
   * You can also omit this and specify HTML content yourself.
   * If you specify both, the content is ignored and this property is used instead.
   * @example <eon-ui-button text="CONTENT"/>
   * @example <eon-ui-button>CONTENT</eon-ui-button>
   */
  @Prop() buttonText: string;

  private get aggregatorItemsLength(): number {
    return this.contentAggregatorItems.length;
  }

  private handleCountOnDesktop(): number {
    return isDesktopMedium() || isDesktopLarge() ? this.countOnDesktop : isTablet() || isDesktopSmall() ? 2 : 1;
  }

  componentWillLoad() {
    this.applyScheme();
    this.contentAggregatorItems = Array.from(this.hostElement.children);
    detectSlotChanges(() => {
      this.handleLayoutChange();
    }, this.hostElement);
    this.handleHeadlineHtmlTag();
    this.defaultProperties();
    this.createDivWrapper();
  }

  componentDidLoad() {
    this.setCssVariables();
    if (this.layout === CONTENT_AGGREGATOR_LAYOUT_TILE) {
      this.handleItemsPerGrid();
      this.lastTileIndex = this.itemsPerGrid;
    }
    this.onWindowresize();
  }

  @Watch("layout")
  handleLayoutChange() {
    this.contentAggregatorItems.map((item) => {
      item.setAttribute(
        "variant",
        this.layout === CONTENT_AGGREGATOR_LAYOUT_TILE || this.layout === CONTENT_AGGREGATOR_LAYOUT_CAROUSEL
          ? CONTENT_AGGREGATOR_ITEM_LAYOUT_TILE
          : this.layout === CONTENT_AGGREGATOR_LAYOUT_LIST_EXPANDED
          ? CONTENT_AGGREGATOR_ITEM_LAYOUT_LIST_EXPANDED
          : CONTENT_AGGREGATOR_ITEM_LAYOUT_LIST
      );
    });
  }

  @Watch("headlineHtmlTag")
  handleHeadlineHtmlTag() {
    if (this.headlineHtmlTag) {
      this.aggregatorHeadlineHtmlTag = this.headlineHtmlTag;
    } else {
      this.aggregatorHeadlineSize =
        this.layout === CONTENT_AGGREGATOR_LAYOUT_CAROUSEL
          ? this.size === "large"
            ? "h3"
            : "h4"
          : this.size === "large"
          ? "h5"
          : "copy";
      this.aggregatorHeadlineHtmlTag = this.aggregatorHeadlineSize;
    }
  }

  @Watch("showAbstract")
  @Watch("dateInput")
  @Watch("hideTime")
  @Watch("timeZone")
  @Watch("twelveHourFormat")
  @Watch("size")
  @Watch("headlineHtmlTag")
  @Watch("align")
  @Watch("zebra")
  @Watch("sameHeight")
  @Watch("layout")
  defaultProperties() {
    // Below code implements consistent properties all over the News Teasers placed inside Carousel Component
    this.contentAggregatorItems.forEach((item, index) => {
      item.setAttribute("hide-time", this.hideTime ? "true" : "false");
      item.setAttribute("time-zone", this.timeZone ?? "");
      item.setAttribute("twelve-hour-format", this.twelveHourFormat ? "true" : "false");
      item.setAttribute("show-abstract", this.showAbstract ? "true" : "false");
      item.setAttribute("date-input", this.dateInput ? "true" : "false");
      item.setAttribute("headline-html-tag", this.aggregatorHeadlineHtmlTag);
      item.setAttribute("headline-size", this.size);
      item.setAttribute("align", this.align);

      if (this.zebra) {
        const nearestBackground = getColorOfNearestBackground(this.hostElement);

        item.setAttribute("scheme", nearestBackground.split("-")[1]);
        item.setAttribute("zebra", this.zebra.toString());

        if (index % 2 === 0) {
          item.setAttribute("shade", "true");
        }
      }

      item.setAttribute(
        "align-layout",
        this.alignLayout === CONTENT_AGGREGATOR_ITEM_LAYOUT_ALIGNMENT_ALTERNATE
          ? index % 2 === 0
            ? CONTENT_AGGREGATOR_ITEM_LAYOUT_ALIGNMENT_LEFT
            : CONTENT_AGGREGATOR_ITEM_LAYOUT_ALIGNMENT_RIGHT
          : this.alignLayout
      );
      item.setAttribute("style", this.sameHeight ? "height: 100%" : "");
      item.setAttribute(
        "variant",
        this.layout === CONTENT_AGGREGATOR_LAYOUT_TILE || this.layout === CONTENT_AGGREGATOR_LAYOUT_CAROUSEL
          ? CONTENT_AGGREGATOR_ITEM_LAYOUT_TILE
          : this.layout === CONTENT_AGGREGATOR_LAYOUT_LIST_EXPANDED
          ? CONTENT_AGGREGATOR_ITEM_LAYOUT_LIST_EXPANDED
          : CONTENT_AGGREGATOR_ITEM_LAYOUT_LIST
      );
      item.setAttribute("headline-character-limit", `${this.headlineCharacterLimit}`);
      item.setAttribute("copy-character-limit", `${this.copyCharacterLimit}`);
    });
    this.onWindowresize();
  }

  /**
   * Wraps newly created DIV element around each News-teaser
   */
  createDivWrapper() {
    this.contentAggregatorItems.forEach((item) => {
      let childWrapper = document.createElement("div");
      childWrapper.className = `${pfx("news-teaser-item")}`;
      item.parentNode.insertBefore(childWrapper, item);
      childWrapper.appendChild(item);
      this.newsTeaserWrapper.push(childWrapper);
    });
    this.handleTeaserSchemeOnDifferentBackground();
  }

  @Watch("layout")
  @Watch("whiteColorScheme")
  handleTeaserSchemeOnDifferentBackground() {
    /**
     * News Teaser scheme follows below flexible colour scheme sequence when placed on white background
     * Ex: limeyellow | turquoise | bordeaux | red | ultralightgrey
     * News Teaser scheme is set to white when placed on colourful background except white
     */
    const nearestBackground = getColorOfNearestBackground(this.hostElement);
    const colors = CONTENT_AGGREGATOR_COLORFUL_VARIANT_COLORS;
    let itemCount = 0;

    if (nearestBackground === CONTENT_AGGREGATOR_BACKGROUND_FOR_COLORFUL_VARIANT) {
      for (let item of this.contentAggregatorItems) {
        item.setAttribute(
          "scheme",
          this.layout === CONTENT_AGGREGATOR_LAYOUT_CAROUSEL || this.layout === CONTENT_AGGREGATOR_LAYOUT_TILE
            ? this.whiteColorScheme
              ? CONTENT_AGGREGATOR_TILE_BRIGHT_COLOR_SCHEME
              : colors[itemCount % colors.length]
            : CONTENT_AGGREGATOR_TILE_DEFAULT_COLOR_SCHEME
        );
        itemCount++;
      }
    }
    this.onWindowresize();
  }

  @Watch("gridSize")
  handleItemsPerGrid(): void {
    if (this.gridSize === CONTENT_AGGREGATOR_LARGE_GRID_SIZE) {
      this.itemsPerGrid =
        isDesktopMedium() || isDesktopLarge() ? TWELVE_ITEMS : isTablet() || isDesktopSmall() ? EIGHT_ITEMS : SIX_ITEMS;
    } else {
      this.itemsPerGrid =
        isDesktopMedium() || isDesktopLarge() ? SIX_ITEMS : isTablet() || isDesktopSmall() ? FOUR_ITEMS : THREE_ITEMS;
    }

    this.lastTileIndex = !this.lastTileIndex ? this.itemsPerGrid : this.lastTileIndex;
  }

  @Listen("resize", { target: "window" })
  @Watch("countOnDesktop")
  @Watch("whiteColorScheme")
  @Watch("totalItems")
  onWindowresize() {
    this.carouselItemWrapper = [];
    this.carouselItems = [];
    this.tileItemWrapper = [];
    this.tileRowWrapper = [];
    if (this.layout === CONTENT_AGGREGATOR_LAYOUT_TILE) {
      // determines items per grid by view size
      this.handleItemsPerGrid();

      // detect last loaded tile item index
      this.handleLastTileIndex();
      /**
       * gridFormation() creates the grid layout and deals with filling up empty spaces, when number of columns
       * per row is less than the count per tile item depending on every viewport. (odd-even count)
       */
      this.gridFormation();
    } else if (
      this.layout === CONTENT_AGGREGATOR_LAYOUT_LIST ||
      this.layout === CONTENT_AGGREGATOR_LAYOUT_LIST_EXPANDED
    ) {
      this.contentAggregatorItems.map((item) => {
        this.carouselItems.push(<eon-ui-list-item innerHTML={`${item.outerHTML}`}></eon-ui-list-item>);
      });
    } else {
      /**
       * adjustCarouselVoidSpace() deals with filling up empty spaces, when number of news teaser items
       * per carousel-item is less than the count per carousel-item depending on every viewport. (odd-even count)
       */
      this.adjustCarouselVoidSpace();
      // Final step to generate a Carousel
      this.carouselFormation();
    }
  }

  @Watch("whiteColorScheme")
  adjustCarouselVoidSpace() {
    // This variable counts number of Carousel-item in a Carousel component
    let numberOfCarouselItem = 0;

    // Defined count of News-Teasers to be shown per carousel-item on every viewport.
    // Mobile: 1, Tablet: 2, Small Desktop: 2, Medium and Large Desktop: 2 | 3
    const contentPerCarouselItem = this.handleCountOnDesktop();

    // Collects the count of News-Teasers per Carousel-item depending on contentPerCarouselItem
    for (let i = 0; i < this.newsTeaserWrapper.length; i++) {
      if (i % contentPerCarouselItem === 0) {
        this.carouselItemWrapper.push(this.newsTeaserWrapper.slice(i, contentPerCarouselItem + i));
        numberOfCarouselItem++;
      }
    }
    const lastCarouselItem =
      this.carouselItemWrapper[numberOfCarouselItem - 1] && this.carouselItemWrapper[numberOfCarouselItem - 1].length;
    if (lastCarouselItem !== contentPerCarouselItem) {
      for (let i = 0; i < contentPerCarouselItem - lastCarouselItem; i++) {
        /**
         * if lastCarouselItem has an empty container for News Teaser due to odd count then a
         * new DIV wrapper is created to fill in the space for proper design adjustment
         */
        let childWrapper = document.createElement("div");
        childWrapper.className = `${pfx("news-teaser-item")}`;
        this.carouselItemWrapper[numberOfCarouselItem - 1].push(childWrapper);
      }
    }
  }

  carouselFormation() {
    this.carouselItemWrapper.forEach((eachCarouselItem) => {
      let teasersOnEachCarouselItem = "";
      /**
       * Collects News-teaser components per carousel-item in DIV: <div class="news-teaser-container">
       * depending on the count per carousel-item should have in every viewport stored in "contentPerCarouselItem"
       */
      eachCarouselItem.map((teaser) => {
        teasersOnEachCarouselItem += teaser.outerHTML;
      });
      const newsTeaserContainer = `<div class="${pfx("news-teaser-container")} ${pfx(
        `${this.layout === "list" ? "list" : "carousel"}-layout`
      )}">${teasersOnEachCarouselItem}</div>`;
      this.carouselItems.push(<eon-ui-carousel-item innerHTML={newsTeaserContainer}></eon-ui-carousel-item>);
    });
  }

  @Watch("gridSize")
  reInitiateGrid() {
    this.lastTileIndex = this.itemsPerGrid;
    this.visibleTileItems = [];
    this.onWindowresize();
  }

  private increaseTileItems(): void {
    this.lastTileIndex += this.itemsPerGrid;
    this.onWindowresize();
  }

  private handleLastTileIndex(): void {
    // Defined count of tile items to be shown per row.
    // Mobile: 1, Tablet: 2, Small Desktop: 2, Medium and Large Desktop: 2 | 3
    const contentPerRowItem = this.handleCountOnDesktop();

    if (this.lastTileIndex > this.aggregatorItemsLength) {
      this.lastTileIndex = this.visibleTileItems.length > 0 ? this.visibleTileItems.length : this.aggregatorItemsLength;
    } else if (this.lastTileIndex < this.itemsPerGrid && this.lastTileIndex % contentPerRowItem !== 0) {
      for (let i = 0; i <= this.lastTileIndex % contentPerRowItem; i++) {
        this.lastTileIndex++;
      }
    }
  }

  gridFormation(): void {
    // determines visible tile items by last loaded index & total items length
    if (
      this.visibleTileItems.length < this.lastTileIndex &&
      this.visibleTileItems.length < this.aggregatorItemsLength
    ) {
      this.contentAggregatorItems.forEach((item) => {
        this.visibleTileItems.push(item);
      });
    }

    // deals with filling up empty spaces
    this.adjustGridVoidSpaces();

    this.tileRowWrapper.forEach((row) => {
      this.carouselItems.push(row);
    });
  }

  adjustGridVoidSpaces(): void {
    // Defined count of tile items to be shown per row.
    // Mobile: 1, Tablet: 2, Small Desktop: 2, Medium and Large Desktop: 2 | 3
    const contentPerRowItem = this.handleCountOnDesktop();

    const contentGridColumn =
      isDesktopMedium() || isDesktopLarge()
        ? "grid-column-4"
        : isTablet() || isDesktopSmall()
        ? "grid-column-6"
        : "grid-column-12";

    this.visibleTileItems.map((item, index) => {
      index++;
      this.tileItemWrapper.push(
        <div class={pfx("news-teaser-grid__column " + contentGridColumn)} innerHTML={`${item.outerHTML}`}></div>
      );

      if (index % contentPerRowItem === 0 || index === this.visibleTileItems.length) {
        if (this.tileItemWrapper.length < contentPerRowItem) {
          for (let i = 0; i <= contentPerRowItem - this.tileItemWrapper.length; i++) {
            /**
             * if last tile item in row has an empty container for tile item due to odd count then a
             * new DIV wrapper is created to fill in the space for proper design adjustment
             */
            this.tileItemWrapper.push(
              <div
                innerHTML={`${
                  typeof this.contentAggregatorItems[index] !== "undefined"
                    ? this.contentAggregatorItems[index].outerHTML
                    : ""
                }`}
                class={pfx("news-teaser-grid__column " + contentGridColumn)}
              ></div>
            );
            index++;
          }
        }

        // prevent empty columns if more items are available on window resize
        if (this.lastTileIndex % contentPerRowItem !== 0) {
          this.lastTileIndex++;
        }

        this.tileRowWrapper.push(
          <div
            data-visible={
              index <= this.lastTileIndex || this.lastTileIndex === this.aggregatorItemsLength ? "true" : "false"
            }
            class={pfx({
              "news-teaser-grid__control": true,
              "news-teaser-grid__control__last": index === this.lastTileIndex
            })}
          >
            {this.tileItemWrapper}
          </div>
        );
        this.tileItemWrapper = [];
      }
    });
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  render() {
    let content;

    if (this.layout === CONTENT_AGGREGATOR_LAYOUT_LIST || this.layout === CONTENT_AGGREGATOR_LAYOUT_LIST_EXPANDED) {
      content = (
        <eon-ui-list page-sizes={this.pageSizes} total-items={this.totalItems}>
          <eon-ui-preloader-circle slot="preloader"></eon-ui-preloader-circle>
          {this.carouselItems}
        </eon-ui-list>
      );
    } else if (this.layout === CONTENT_AGGREGATOR_LAYOUT_TILE) {
      content = (
        <div class={pfx("news-teaser-container tile-layout")}>
          {this.carouselItems}
          {(() => {
            if (this.lastTileIndex < this.aggregatorItemsLength) {
              return (
                <div class={pfx("news-teaser-paging")}>
                  <div class={pfx("news-teaser-paging__column")}>
                    <eon-ui-button
                      text={this.buttonText}
                      aria-label={this.buttonText}
                      icon="more"
                      full-width-on-mobile="true"
                      onClick={() => this.increaseTileItems()}
                    ></eon-ui-button>
                  </div>
                </div>
              );
            }
          })()}
        </div>
      );
    } else {
      content = (
        <eon-ui-carousel same-height={this.sameHeight} controls={false} scheme={this.scheme} autoplay={this.autoplay}>
          {this.carouselItems}
        </eon-ui-carousel>
      );
    }

    return <Host>{content}</Host>;
  }
}
